import React, {useEffect, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'
import {
  Button,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import {makeStyles} from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import DatePicker from 'react-datepicker'
import {
  Agency,
  getListAgencyApi,
  getListDeviceApi,
  PostRegisterMember,
  postRegisterMemberApi,
} from 'api/orgApi'
import {fetchTerms} from 'api/commonApi'
import CardWithTitle from 'components/atoms/CardWithTitle'
import RegisterCardWithTitle from 'components/atoms/RegisterCardWithTitle'
import {useBoldInputStyle} from 'components/common/useBoldInputStyle'
import NoticeConfirmDialog from 'components/Dialog/NoticeConfirmDialog'
import useAuth from 'features/auth/useAuth'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'
import RegisterStyle from 'pages/CustomerManagement/MemberRegistration/Style'
import TableHospitalInformation, {
  SelectedListType,
} from 'pages/CustomerManagement/MemberRegistration/Form/TableHospitalInfo'
import AddressInput from 'pages/CustomerManagement/MemberRegistration/Form/AddressInput'
import BusinessNoInput from 'pages/CustomerManagement/MemberRegistration/Form/BusinessNoInput'
import EmailInput from 'pages/CustomerManagement/MemberRegistration/Form/EmailInput'
import PasswordNoConfirmInput from 'pages/CustomerManagement/MemberRegistration/Form/PasswordNoConfirmInput'
import NursingInstitutionInput from 'pages/CustomerManagement/MemberRegistration/Form/InstitutionInput'
import OrganizationNameInput from 'pages/CustomerManagement/MemberRegistration/Form/OrganizationNameInput'
import SelectedIndustry from 'pages/CustomerManagement/MemberRegistration/Form/SelectedIndustry'
import TelephoneNumberInput from 'pages/CustomerManagement/MemberRegistration/Form/TelephoneNumberInput'
import UserNameInput from 'pages/CustomerManagement/MemberRegistration/Form/UserNameInput'
import CountrySelect from 'pages/CustomerManagement/MemberRegistration/Form/CountrySelect'
import RouteConstant from 'constants/RouteConstant'
import {Add, CloseRounded} from '@material-ui/icons'
import AgencyInput from './Form/AgencyInput'

const useStyles = makeStyles(RegisterStyle)

export default function MemberRegistrationPage() {
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const inputClasses = useBoldInputStyle()
  const {loading} = useAuth()
  const [policyChecked, setPolicyChecked] = useState(false)
  const [privacyChecked, setPrivacyChecked] = useState(false)
  const [privacyUrl, setPrivacyUrl] = React.useState('')
  const [serviceUrl, setServiceUrl] = React.useState('')
  const [selectedDetails, setSelectedDetails] = useState<SelectedListType[]>([])
  const [minDate, setMinDate] = useState<Date>()
  const [packageStartDate, setPackageStartDate] = useState<Date | null>(null)
  const [packageExpireDate, setPackageExpireDate] = useState<Date | null>(null)
  const [listManufacturer, setListManufacturer] = useState<string[]>([])
  const [listAgencies, setListAgencies] = useState<Agency[]>([])
  const [listDevice, setListDevice] = useState<
    Record<
      string,
      {
        deviceId: number
        deviceName: string
      }[]
    >
  >({})
  const [listDeviceSelected, setListDeviceSelected] = useState<
    {
      manufacturer: string
      deviceId: number
    }[]
  >([
    {
      manufacturer: '',
      deviceId: 0,
    },
  ])
  const [twoFactorType, setTwoFactorType] = useState(false)
  const [orgType, setOrgType] = useState(false)
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {onOpen: onSuccessModalOpen} = useSuccessModal()
  const [isUsingEdfDevice, setIsUsingEdfDevice] = useState(false)
  const [isPerformAsAgency, setIsPerformAsAgency] = useState(false)
  const history = useHistory()
  const createMemberInfo = useForm<PostRegisterMember>()
  const {
    setValue,
    setError,
    clearErrors,
    formState: {errors},
  } = createMemberInfo
  const [key, setKey] = useState(1)

  const checkServiceHandler = () => {
    setPolicyChecked(!policyChecked)
    setValue('policyYn', policyChecked ? 'Y' : 'N')
    clearErrors('policyYn')
  }
  const checkPrivacyHandler = () => {
    setPrivacyChecked(!privacyChecked)
    setValue('privacyYn', privacyChecked ? 'Y' : 'N')
    clearErrors('privacyYn')
  }
  const convertStringFromDateTime = (data: Date | null) => {
    if (data) {
      const date = new Date(data)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    return ''
  }

  const handleValueStartDate = (data: Date | null) => {
    if (data) {
      const newDate = new Date(data)
      newDate.setDate(newDate.getDate() + 1)
      setMinDate(newDate)
      if (packageExpireDate && data >= packageExpireDate) {
        setPackageExpireDate(null)
      }
    } else {
      const newDate = new Date()
      if (packageExpireDate && new Date() >= packageExpireDate) {
        setPackageExpireDate(null)
      }
      newDate.setDate(newDate.getDate() + 1)
      setMinDate(newDate)
    }
    setPackageStartDate(data)
  }
  const handleValueExpireDate = (data: Date | null) => {
    setPackageExpireDate(data)
  }
  const handleRegister = async (orgInfo: PostRegisterMember) => {
    try {
      const response = await postRegisterMemberApi(orgInfo)
      if (response)
        onSuccessModalOpen(response.message).then(() => {
          history.push(`${RouteConstant.HOME.path}`)
        })
    } catch (e) {
      onFailureModalOpen(e?.message ?? t('IRegisterFail'))
    }
  }
  const handleCreate = (orgData: PostRegisterMember) => {
    handleRegister(orgData)
  }
  const submit = (data: PostRegisterMember) => {
    clearErrors('agencyId')
    if (!privacyChecked) {
      setError('privacyYn', {
        type: 'custom',
        message: `${t('IRequired')}`,
      })
      onFailureModalOpen(t('IRegisterTermsNotAgree'))
      return
    }
    if (!policyChecked) {
      setError('policyYn', {
        type: 'custom',
        message: `${t('IRequired')}`,
      })
      onFailureModalOpen(t('IRegisterTermsNotAgree'))
      return
    }
    if (
      isUsingEdfDevice &&
      listDeviceSelected.some((item) => item.deviceId === 0)
    ) {
      onFailureModalOpen(t('ISelectDevice'))
      return
    }

    if (isPerformAsAgency && data.agencyId === -1) {
      setError('agencyId', {
        type: 'custom',
        message: `${t('IRequired')}`,
      })
      return
    }

    const arrProductIds = selectedDetails.map((item) => item.productId)
    if (!packageStartDate && packageExpireDate) {
      data.packageStartDate = convertStringFromDateTime(new Date())
    } else {
      data.packageStartDate = convertStringFromDateTime(packageStartDate)
    }
    data.packageExpireDate = convertStringFromDateTime(packageExpireDate)
    handleCreate({
      address: data.customerPo,
      area: data.customerPo,
      agencyId: isPerformAsAgency ? data.agencyId : null,
      businessNo: data.businessNo,
      countryId: data.countryId,
      detailIndustry: data.industryDetail,
      edfYn: data.edfYn ? 'Y' : 'N',
      email: data.email,
      firstUserName: data.customerFistN,
      lastUserName: data.lastUserName,
      industry: data.industry,
      institutionNo: data.institutionNo,
      privacyYn: 'Y',
      policyYn: 'Y',
      orgName: data.orgName,
      orgType: data.orgType ? data.orgType : 'NORMAL',
      packageExpireDate: data.packageExpireDate ? data.packageExpireDate : null,
      packageStartDate: data.packageStartDate ? data.packageStartDate : null,
      password: data.password,
      tel: data.tel,
      therapyYn: data.therapyYn ? 'Y' : 'N',
      healthProductIds: arrProductIds.length > 0 ? arrProductIds : null,
      twoFactorType,
      externalEdfYn: isUsingEdfDevice ? 'Y' : 'N',
      ...(isUsingEdfDevice && {
        deviceIds: Array.from(
          new Set(listDeviceSelected.map((item) => item.deviceId)),
        ),
      }),
    } as PostRegisterMember)
  }

  const handleCheckboxProductType = (details: SelectedListType[]) => {
    setSelectedDetails([...details])
  }

  const handleCheckboxChangeOrgType = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked
    setOrgType(isChecked)
    if (isChecked) {
      setTwoFactorType(true)
      createMemberInfo.setValue('twoFactorType', true)
    } else setTwoFactorType(false)
  }
  const handle2FACheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked
    setTwoFactorType(isChecked)
    createMemberInfo.setValue('twoFactorType', isChecked)
  }

  const getListCalibrationDevice = async () => {
    const res = await getListDeviceApi()
    const listDuplicatedManu = res.list.map((item) => item.manufacturer)
    setListManufacturer(Array.from(new Set(listDuplicatedManu)))
    const calibrationDeviceList = res.list.reduce<
      Record<
        string,
        {
          deviceId: number
          deviceName: string
        }[]
      >
    >((acc, cur) => {
      if (!acc[cur.manufacturer]) {
        acc[cur.manufacturer] = []
      }
      acc[cur.manufacturer].push({
        deviceId: cur.deviceId,
        deviceName: cur.deviceName,
      })
      return acc
    }, {})
    setListDevice(calibrationDeviceList)
  }

  const getListAgency = async () => {
    const res = await getListAgencyApi()
    setListAgencies(res.list)
  }

  const onSelectDevice = (
    value: string,
    index: number,
    type: 'manufacturer' | 'deviceId',
  ) => {
    const newList = [...listDeviceSelected]
    if (type === 'manufacturer') {
      newList[index].manufacturer = value
      newList[index].deviceId = 0
    } else {
      newList[index].deviceId = Number(value)
    }
    setListDeviceSelected(newList)
  }

  const onDeleteDevice = (index: number) => {
    const newList = [...listDeviceSelected]
    newList.splice(index, 1)
    setListDeviceSelected(newList)
  }

  const handleTerms = async (termsRequest: 'PRIVACY' | 'SERVICE') => {
    try {
      const response = await fetchTerms({
        termsLang: i18n.language,
        type: termsRequest,
      })
      if (response.success && termsRequest === 'PRIVACY') {
        setPrivacyUrl(response.data)
      }
      if (response.success && termsRequest === 'SERVICE') {
        setServiceUrl(response.data)
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    handleTerms('PRIVACY')
    handleTerms('SERVICE')
  }, [i18n.language])

  useEffect(() => {
    createMemberInfo.setValue('orgType', orgType ? 'HEALTH' : 'NORMAL')
    if (!orgType) {
      setSelectedDetails([])
    }
  }, [orgType])

  useEffect(() => {
    const newDate = new Date()
    newDate.setDate(newDate.getDate() + 1)
    setMinDate(newDate)
    getListCalibrationDevice()
    getListAgency()
    setKey(key + 1)
  }, [])

  return (
    <div key={key}>
      {loading && <div>loading</div>}
      <div className={classes.topRoot}>
        <FormProvider {...createMemberInfo}>
          <form
            onSubmit={createMemberInfo.handleSubmit((data) =>
              submit(data as PostRegisterMember),
            )}
          >
            <div className={classes.container}>
              <div className={classes.agreeSectionContainer}>
                <RegisterCardWithTitle
                  title={t('IRegisterPrivacyAgree')}
                  className={classes.termsWrap}
                >
                  <iframe
                    className={classes.termsContainer}
                    title='Privacy information policy'
                    src={privacyUrl}
                  />
                </RegisterCardWithTitle>
                <div className={classes.serviceCheckboxWrap}>
                  <Checkbox
                    id='check'
                    color='primary'
                    disableRipple
                    checked={privacyChecked}
                    inputProps={{'aria-label': 'decorative checkbox'}}
                    onChange={() => checkPrivacyHandler()}
                  />
                  <span className={classes.termsTitle}>
                    {t('IRegisterPrivacyAgreeNotice')}
                  </span>
                </div>
                {errors.privacyYn && (
                  <span className={inputClasses.errorText}>
                    {t('IRequired')}
                  </span>
                )}
                <Typography className={classes.padding} />
                <RegisterCardWithTitle
                  title={t('IRegisterServiceAgree')}
                  className={classes.termsWrap}
                >
                  <iframe
                    className={classes.termsContainer}
                    title='My Daily Marathon Tracker'
                    src={serviceUrl}
                  />
                </RegisterCardWithTitle>

                <div className={classes.serviceCheckboxWrap}>
                  <Checkbox
                    color='primary'
                    checked={policyChecked}
                    disableRipple
                    inputProps={{'aria-label': 'decorative checkbox'}}
                    onChange={() => checkServiceHandler()}
                  />
                  <span className={classes.termsTitle}>
                    {t('IRegisterServiceAgreeNotice')}
                  </span>
                </div>
                {errors.policyYn && (
                  <span className={inputClasses.errorText}>
                    {t('IRequired')}
                  </span>
                )}
              </div>

              <CardWithTitle
                title={t('IUserInfo')}
                className={classes.formRegister}
              >
                <div className={classes.inputContainer}>
                  <div style={{height: 24}}>
                    <Checkbox
                      color='primary'
                      checked={orgType}
                      onChange={handleCheckboxChangeOrgType}
                      style={{height: 24, paddingLeft: 0}}
                    />
                    {t('INoteCenter')}
                  </div>
                  <OrganizationNameInput />
                  <UserNameInput />
                  <EmailInput />
                  <PasswordNoConfirmInput />
                  <CountrySelect />
                  <AddressInput />
                  <TelephoneNumberInput />
                  <SelectedIndustry />
                  <BusinessNoInput />
                  <NursingInstitutionInput />
                  {isPerformAsAgency && (
                    <AgencyInput listAgency={listAgencies} />
                  )}
                  {/* <div>
                    <p
                      className={inputClasses.inputLabel}
                      style={{padding: '5px 0'}}
                    >
                      {t('IAccountUsagePeriod')}
                    </p>
                    <div className={inputClasses.inputContainer}>
                      <div className={inputClasses.inputWrap}>
                        <DatePicker
                          selected={packageStartDate}
                          onChange={(date) => handleValueStartDate(date)}
                          placeholderText='yyyy-MM-dd'
                          className={inputClasses.input}
                        />
                      </div>
                      <div className={inputClasses.inputWrap}>
                        <DatePicker
                          selected={packageExpireDate}
                          onChange={(date) => handleValueExpireDate(date)}
                          placeholderText='yyyy-MM-dd'
                          minDate={minDate}
                          className={inputClasses.input}
                        />
                      </div>
                    </div>
                  </div> */}
                  <div
                    style={{
                      display: 'grid',
                      alignItems: 'center',
                      width: '100%',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      marginTop: '5px',
                    }}
                  >
                    <div style={{height: 24}}>
                      <Checkbox
                        color='primary'
                        style={{height: 24, paddingLeft: 0}}
                        {...createMemberInfo.register('therapyYn')}
                      />
                      {t('IEnablePBMFeature')}
                    </div>
                    <div style={{height: 24}}>
                      <Checkbox
                        color='primary'
                        style={{height: 24, paddingLeft: 0}}
                        checked={twoFactorType}
                        onChange={handle2FACheckbox}
                        disabled={!orgType}
                      />
                      {t('IEmailVerification')}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '5px',
                      }}
                    >
                      <Checkbox
                        color='primary'
                        style={{height: 24, paddingLeft: 0}}
                        checked={isUsingEdfDevice}
                        onChange={(e) => setIsUsingEdfDevice(e.target.checked)}
                      />
                      {t('IUsingDeviceEDF')}
                      {isUsingEdfDevice && (
                        <Button
                          variant='contained'
                          color='primary'
                          disableElevation
                          type='button'
                          style={{
                            marginLeft: 20,
                          }}
                          disabled={listDeviceSelected.length >= 10}
                          onClick={() =>
                            setListDeviceSelected((prev) => [
                              ...prev,
                              ...[
                                {
                                  manufacturer: '',
                                  deviceId: 0,
                                },
                              ],
                            ])
                          }
                        >
                          <Add style={{fontSize: 16, marginRight: 4}} />
                          {t('IAdd')}
                        </Button>
                      )}
                    </div>
                    <div style={{height: 24, marginTop: 5}}>
                      <Checkbox
                        color='primary'
                        style={{height: 24, paddingLeft: 0}}
                        checked={isPerformAsAgency}
                        onChange={(e) => setIsPerformAsAgency(e.target.checked)}
                      />
                      {t('IPerformAsAgency')}
                    </div>
                  </div>
                  {isUsingEdfDevice && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        marginTop: 10,
                      }}
                    >
                      {listDeviceSelected.map((item, index) => (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 20,
                          }}
                        >
                          {t('IDevice')}:
                          <Select
                            value={item.manufacturer}
                            label={t('IManufacturingCompany')}
                            id='manufacturer-select'
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected || selected === '') {
                                return (
                                  <span style={{color: '#9e9e9e'}}>
                                    {t('IManufacturingCompany')}
                                  </span>
                                )
                              }
                              return String(selected)
                            }}
                            onChange={(e) => {
                              onSelectDevice(
                                String(e.target.value),
                                index,
                                'manufacturer',
                              )
                            }}
                            style={{
                              width: 200,
                              height: 40,
                            }}
                            input={
                              <OutlinedInput
                                name='manufacturer'
                                id='manufacturer-select'
                              />
                            }
                          >
                            {listManufacturer.map((item) => (
                              <MenuItem value={item} key={item}>
                                {item}
                              </MenuItem>
                            ))}
                          </Select>
                          <Select
                            value={item.deviceId}
                            label={t('IModelName')}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected || Number(selected) === 0) {
                                return (
                                  <span style={{color: '#9e9e9e'}}>
                                    {t('IModelName')}
                                  </span>
                                )
                              }

                              const selectedDevice = (
                                listDevice[item.manufacturer] ?? []
                              ).find(
                                (device) =>
                                  device.deviceId === Number(selected),
                              )
                              return (
                                selectedDevice?.deviceName ?? String(selected)
                              )
                            }}
                            onChange={(e) => {
                              onSelectDevice(
                                String(e.target.value),
                                index,
                                'deviceId',
                              )
                            }}
                            style={{
                              width: 200,
                              height: 40,
                            }}
                            input={
                              <OutlinedInput name='model' id='model_name' />
                            }
                          >
                            {(listDevice[item.manufacturer] ?? []).map(
                              (item) => (
                                <MenuItem
                                  value={item.deviceId}
                                  key={item.deviceId}
                                >
                                  {item.deviceName}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                          {listDeviceSelected.length > 1 && (
                            <CloseRounded
                              style={{
                                cursor: 'pointer',
                                color: 'red',
                              }}
                              onClick={() => onDeleteDevice(index)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <Typography className={classes.padding} />
                  {/* {orgType && (
                    <div className={classes.tableFlexBox}>
                      <TableHospitalInformation
                        products={selectedDetails}
                        onCheckboxChange={handleCheckboxProductType}
                      />
                    </div>
                  )} */}
                  <div className={classes.submitButtonContainer}>
                    <Button
                      variant='contained'
                      color='primary'
                      disableElevation
                      type='submit'
                    >
                      {t('IRegister')}
                    </Button>
                  </div>
                </div>
              </CardWithTitle>
            </div>
          </form>
        </FormProvider>
      </div>
      <NoticeConfirmDialog />
    </div>
  )
}
